/* eslint-disable react/prefer-stateless-function */
import React, { useContext } from "react"
import {
  createStyles,
  createCSS,
  createElement,
  forwardRef,
} from "@design-system/styled-system"
import { TextProps } from "./types"
import { TextAncestorContext } from "./TextAncestorContext"

const classes = createCSS({
  text: {
    border: "0 solid black",
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    overflowWrap: "break-word",
    wordWrap: "break-word",
  },
  textHasAncestor: {
    color: "inherit",
    font: "inherit",
    whiteSpace: "inherit",
  },
  textOneLine: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  textMultiLine: {
    display: "-webkit-box",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    WebkitBoxOrient: "vertical",
  },
})

const styles = createStyles((theme) => ({
  root: {
    lineHeight: theme.lineHeights.medium,
    fontFamily: theme.fonts.body,
  },
  notSelectable: {
    userSelect: "none",
  },
  selectable: {
    userSelect: "text",
  },
}))

const textAlignStyles = createStyles({
  center: {
    textAlign: "center",
  },
  left: {
    textAlign: "left",
  },
  right: {
    textAlign: "right",
  },
})

const sizeStyles = createStyles((theme) => ({
  small: {
    fontSize: theme.textFontSizes.small,
    lineHeight: theme.textLineHeights.small,
  },
  medium: {
    fontSize: theme.textFontSizes.medium,
    lineHeight: theme.textLineHeights.medium,
  },
  large: {
    fontSize: theme.textFontSizes.large,
    lineHeight: theme.textLineHeights.large,
  },
}))

export const Text = forwardRef<TextProps, "div">((props, ref) => {
  const { numberOfLines, selectable, size, align, ...others } = props

  const hasTextAncestor = useContext(TextAncestorContext)

  const classList = [
    classes.text,
    hasTextAncestor === true && classes.textHasAncestor,
    numberOfLines === 1 && classes.textOneLine,
    numberOfLines != null && numberOfLines > 1 && classes.textMultiLine,
  ]
  const style = [
    styles.root,
    numberOfLines != null &&
      numberOfLines > 1 && { WebkitLineClamp: numberOfLines },
    selectable === true && styles.selectable,
    selectable === false && styles.notSelectable,
    align && textAlignStyles[align],
    size && sizeStyles[size],
    props.style,
  ]

  const component = hasTextAncestor ? "span" : "div"

  const textProps = {
    ...others,
    classList,
    style,
    ref,
  }
  const element = createElement(component, textProps)

  return hasTextAncestor ? (
    element
  ) : (
    <TextAncestorContext.Provider value>{element}</TextAncestorContext.Provider>
  )
})

Text.displayName = "Text"
Text.defaultProps = {
  size: "medium",
}
